import { queryOptions, skipToken, type UseQueryResult, useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';

import {
  getWorkspaceAgentAssetListRowClipIds,
  toWorkspaceAgentAssetListItems,
  type WorkspaceAgentAssetListRow,
} from '~/components/v2/components/workspace/workspace-agent-asset-list/utils/toWorkspaceAgentAssetListItems';
import {
  fetchPrivateWorkspaceAgentClipsListByIds,
  getPrivateWorkspaceAgentClipsListByIdsKey,
  type PrivateWorkspaceAgentClipsListByIdsRows,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { usePrivateWorkspaceAgentSessionArtifactRowsInfinite } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionArtifactRowsInfinite';
import { stableListByIdsClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentClipIdsScope';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { galleryViewDataToUpdateMediaPlayer } from '~/swr-hooks/gallery/galleryView/utils';
import { tableViewDataToUpdateMediaPlayer } from '~/swr-hooks/gallery/tableView/utils';
import { type UseGalleryMixedData, type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';
import { usePrivateMediaPlayerDataUpdate } from '~/swr-hooks/mediaPlayer/usePrivateMediaPlayerData';

export type UsePrivateWorkspaceAgentAssetListItemsParams = {
  artifactId: string | undefined;
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentAssetListItems = {
  galleryItemsData: UseGalleryViewItemsReturn<Board, Clip>;
  tableItemsData: UseGalleryMixedData<Board, Clip>;
  /** Rows loaded so far; the artifact carries no declared total. */
  totalCount: number;
  unavailableCount: number;
  /** Rows whose clip lookup failed. Not counted as unavailable: they may well still exist. */
  failedCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  /** The artifact exists but holds something other than assets. */
  isWrongType: boolean;
};

type RowPage = {
  clipIds: string[];
  rows: WorkspaceAgentAssetListRow[];
};

type ClipLookupState = {
  clipsByPage: (PrivateWorkspaceAgentClipsListByIdsRows | undefined)[];
  settledByPage: boolean[];
  /** Settled with an error and nothing to show. A refetch error over existing data still shows it. */
  failedByPage: boolean[];
};

const NO_BOARDS: Board[] = [];
const NO_FILES: Clip[] = [];

/** One entry per lookup, so `useQueries` keeps the same result while nothing settles. */
const combineClipLookups = (results: UseQueryResult<PrivateWorkspaceAgentClipsListByIdsRows>[]): ClipLookupState => ({
  clipsByPage: results.map((result) => result.data),
  settledByPage: results.map((result) => result.isSuccess || result.isError),
  failedByPage: results.map((result) => result.isError && result.data === undefined),
});

/**
 * Pages the agent asset list artifact and resolves each row page with its own `Clips.listByIds`
 * lookup (row pages are sized to that endpoint's cap), so loading the next page does not refetch
 * or blank the clips already on screen.
 */
export const usePrivateWorkspaceAgentAssetListItems = ({
  artifactId,
  sessionId,
  workspaceId,
}: UsePrivateWorkspaceAgentAssetListItemsParams): PrivateWorkspaceAgentAssetListItems => {
  const presentation = useWorkspaceAgentAssetListViewStore((state) => state.presentation);
  const { updatePrivateMediaPlayerData } = usePrivateMediaPlayerDataUpdate();
  const rowsQuery = usePrivateWorkspaceAgentSessionArtifactRowsInfinite({ artifactId, sessionId, workspaceId });
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = rowsQuery;

  const firstPageType = rowsQuery.data?.pages[0]?.type;
  const isWrongType = firstPageType !== undefined && firstPageType !== 'assetList';

  const rowPages = useMemo(
    (): RowPage[] =>
      (rowsQuery.data?.pages ?? []).flatMap((page) =>
        page.type === 'assetList'
          ? [{ clipIds: getWorkspaceAgentAssetListRowClipIds(page.data), rows: page.data }]
          : [],
      ),
    [rowsQuery.data?.pages],
  );

  const clipLookups = useQueries({
    queries: rowPages.map(({ clipIds }) => {
      const params = { clipIds: stableListByIdsClipIds(clipIds), workspaceId };

      return queryOptions({
        queryKey: getPrivateWorkspaceAgentClipsListByIdsKey(params),
        queryFn: workspaceId && clipIds.length > 0 ? () => fetchPrivateWorkspaceAgentClipsListByIds(params) : skipToken,
      });
    }),
    combine: combineClipLookups,
  });

  const items = useMemo(() => {
    // A page with no clip ids never runs a lookup; its rows are already settled as unavailable.
    const hasPageResolved = (page: RowPage, index: number) =>
      page.clipIds.length === 0 || (!!clipLookups.settledByPage[index] && !clipLookups.failedByPage[index]);

    return toWorkspaceAgentAssetListItems({
      clips: clipLookups.clipsByPage.flatMap((clips) => clips ?? []),
      // A failed lookup's rows stay out: passed in, every one would read as deleted content.
      rows: rowPages.flatMap((page, index) => (hasPageResolved(page, index) ? page.rows : [])),
    });
  }, [clipLookups, rowPages]);

  const failedCount = useMemo(
    () =>
      rowPages.reduce(
        (count, page, index) =>
          page.clipIds.length > 0 && clipLookups.failedByPage[index] ? count + page.rows.length : count,
        0,
      ),
    [clipLookups.failedByPage, rowPages],
  );

  const isFirstPageSettled = (rowPages[0]?.clipIds.length ?? 0) === 0 || !!clipLookups.settledByPage[0];
  const isRowsPending = rowsQuery.isPending && !rowsQuery.isError;
  const isLoading = isRowsPending || (!isWrongType && !isFirstPageSettled);
  const isLoadingMore =
    isFetchingNextPage ||
    rowPages.some((page, index) => index > 0 && page.clipIds.length > 0 && !clipLookups.settledByPage[index]);
  // A failed next-page fetch keeps the rows already on screen and stops offering more, rather than
  // blanking the list or retrying on every scroll.
  const hasMore = !isWrongType && !!hasNextPage && !rowsQuery.isFetchNextPageError;
  const isEmpty = !isLoading && !isLoadingMore && !hasMore && items.clips.length === 0;

  const loadNextPage = useCallback(() => {
    if (hasMore && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasMore, isFetchingNextPage]);

  // The gallery only asks for the next page from a rendered section, and holds the files section
  // until the shared cursor finishes so later assets never land above files already on screen.
  // In gallery view, keep paging while no assets have resolved; in table view, only when nothing resolved.
  const needsAutoPage = presentation === 'gallery' ? items.assets.length === 0 : items.clips.length === 0;

  useEffect(() => {
    if (!isLoading && !isLoadingMore && needsAutoPage && hasMore) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasMore, isLoading, isLoadingMore, needsAutoPage]);

  const holdGalleryFiles = hasMore || isLoadingMore;
  const galleryFiles = holdGalleryFiles ? NO_FILES : items.files;

  const galleryItemsData = useMemo(
    (): UseGalleryViewItemsReturn<Board, Clip> => ({
      data: {
        boards: { hasMore: false, isLoading: false, items: NO_BOARDS, total: 0 },
        clips: {
          hasMore,
          isLoading: isLoadingMore,
          items: items.assets,
          total: items.assets.length,
        },
        files: {
          hasMore: false,
          isLoading: false,
          items: galleryFiles,
          total: galleryFiles.length,
        },
      },
      isEmpty,
      isLoading: isLoading || (holdGalleryFiles && items.assets.length === 0),
      loadNextPage,
    }),
    [galleryFiles, hasMore, holdGalleryFiles, isEmpty, isLoading, isLoadingMore, items.assets, loadNextPage],
  );

  const tableItemsData = useMemo(
    (): UseGalleryMixedData<Board, Clip> => ({
      data: items.mixedItems,
      hasMore,
      isEmpty,
      isLoading,
      isLoadingMore,
      loadNextPage,
      total: items.mixedItems.length,
    }),
    [hasMore, isEmpty, isLoading, isLoadingMore, items.mixedItems, loadNextPage],
  );

  useFetchObjectsPermissions({
    objects: {
      assetIds: items.clips.map(({ assetId }) => assetId),
      clipIds: items.clips.map(({ id }) => id),
    },
  });

  useEffect(() => {
    updatePrivateMediaPlayerData(
      presentation === 'gallery'
        ? galleryViewDataToUpdateMediaPlayer(galleryItemsData)
        : tableViewDataToUpdateMediaPlayer(tableItemsData),
    );
  }, [galleryItemsData, presentation, tableItemsData, updatePrivateMediaPlayerData]);

  // Only an error with nothing loaded is an error state; a failed next page is handled by `hasMore`.
  const isError = rowsQuery.isError && !rowsQuery.data;

  // Identity-stable so the page provider's value only changes when a slice does.
  return useMemo(
    () => ({
      failedCount,
      galleryItemsData,
      hasMore,
      isError,
      isLoading,
      isWrongType,
      tableItemsData,
      totalCount: items.totalCount,
      unavailableCount: items.unavailableCount,
    }),
    [failedCount, galleryItemsData, hasMore, isError, isLoading, isWrongType, items, tableItemsData],
  );
};
